/**
 * Window List Component
 *
 * Displays list of maintenance windows grouped by client with recurrence indicators.
 *
 * @module components/SLA/WindowList
 */

import React from 'react';
import { MaintenanceWindow } from '@shared/types/sla';
import { formatEastern, formatEasternDate } from '@/utils/dateFormatter';

interface WindowListProps {
  /** List of windows to display */
  windows: MaintenanceWindow[];
  /** Callback when edit button is clicked */
  onEdit: (window: MaintenanceWindow) => void;
  /** Callback when delete button is clicked */
  onDelete: (windowId: string) => void;
}

const DAYS_OF_WEEK = ['Sunday', 'Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday'];

/**
 * Window List - Display and manage maintenance windows
 */
const WindowList: React.FC<WindowListProps> = ({ windows, onEdit, onDelete }) => {
  const formatTime = (time: string | null) => {
    if (!time) return '-';
    return time;
  };

  const formatDatetime = (datetime: string | null) => {
    if (!datetime) return '-';
    return formatEastern(datetime);
  };

  // Group windows by client
  const windowsByClient = windows.reduce(
    (acc, window) => {
      const clientId = window.client_id;
      if (!acc[clientId]) {
        acc[clientId] = [];
      }
      acc[clientId].push(window);
      return acc;
    },
    {} as Record<string, MaintenanceWindow[]>
  );

  if (windows.length === 0) {
    return (
      <div className='text-center py-12 border border-dashed border-[var(--color-border)] rounded-lg'>
        <svg
          className='mx-auto h-12 w-12 text-[var(--color-text-secondary)]'
          fill='none'
          viewBox='0 0 24 24'
          stroke='currentColor'
        >
          <path
            strokeLinecap='round'
            strokeLinejoin='round'
            strokeWidth={2}
            d='M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z'
          />
        </svg>
        <h3 className='mt-2 text-sm font-medium text-[var(--color-text-primary)]'>No maintenance windows</h3>
        <p className='mt-1 text-sm text-[var(--color-text-secondary)]'>
          Get started by creating a maintenance window for a client.
        </p>
      </div>
    );
  }

  return (
    <div className='space-y-6'>
      {Object.entries(windowsByClient).map(([clientId, clientWindows]) => (
        <div key={clientId} className='bg-[var(--color-bg-card)] rounded-lg shadow overflow-hidden'>
          {/* Client Header */}
          <div className='bg-[var(--color-bg-subtle)] px-4 py-3 border-b border-[var(--color-border)]'>
            <h4 className='text-sm font-medium text-[var(--color-text-primary)]'>
              Client: {clientId}
              <span className='ml-2 text-xs text-[var(--color-text-secondary)]'>
                ({clientWindows.length} window{clientWindows.length !== 1 ? 's' : ''})
              </span>
            </h4>
          </div>

          {/* Windows Table */}
          <div className='overflow-hidden'>
            <table className='min-w-full divide-y divide-[var(--color-border)]'>
              <thead className='bg-[var(--color-bg-subtle)]'>
                <tr>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Type
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Schedule
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Time Window
                  </th>
                  <th className='px-4 py-3 text-left text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Timezone
                  </th>
                  <th className='relative px-4 py-3 text-right text-xs font-medium text-[var(--color-text-secondary)] uppercase tracking-wider'>
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className='bg-[var(--color-bg-card)] divide-y divide-[var(--color-border)]'>
                {clientWindows.map((window) => (
                  <tr key={window.id}>
                    <td className='px-4 py-3 whitespace-nowrap text-sm'>
                      {window.recurrence_type === 'weekly' ? (
                        <span className='inline-flex items-center rounded-full bg-blue-100 dark:bg-blue-900/30 px-2.5 py-0.5 text-xs font-medium text-blue-800 dark:text-blue-300'>
                          <svg className='mr-1 h-3 w-3' fill='currentColor' viewBox='0 0 20 20'>
                            <path
                              fillRule='evenodd'
                              d='M4 2a1 1 0 011 1v2.101a7.002 7.002 0 0111.601 2.566 1 1 0 11-1.885.666A5.002 5.002 0 005.999 7H9a1 1 0 010 2H4a1 1 0 01-1-1V3a1 1 0 011-1zm.008 9.057a1 1 0 011.276.61A5.002 5.002 0 0014.001 13H11a1 1 0 110-2h5a1 1 0 011 1v5a1 1 0 11-2 0v-2.101a7.002 7.002 0 01-11.601-2.566 1 1 0 01.61-1.276z'
                              clipRule='evenodd'
                            />
                          </svg>
                          Weekly
                        </span>
                      ) : (
                        <span className='inline-flex items-center rounded-full bg-yellow-100 dark:bg-yellow-900/30 px-2.5 py-0.5 text-xs font-medium text-yellow-800 dark:text-yellow-300'>
                          One-Time
                        </span>
                      )}
                    </td>
                    <td className='px-4 py-3 whitespace-nowrap text-sm text-[var(--color-text-secondary)]'>
                      {window.recurrence_type === 'weekly' && window.day_of_week !== null
                        ? DAYS_OF_WEEK[window.day_of_week]
                        : window.specific_date
                          ? formatEasternDate(window.specific_date)
                          : '-'}
                    </td>
                    <td className='px-4 py-3 whitespace-nowrap text-sm text-[var(--color-text-secondary)]'>
                      {window.recurrence_type === 'weekly'
                        ? `${formatTime(window.start_time)} - ${formatTime(window.end_time)}`
                        : `${formatDatetime(window.specific_start_datetime)} - ${formatDatetime(window.specific_end_datetime)}`}
                    </td>
                    <td className='px-4 py-3 whitespace-nowrap text-sm text-[var(--color-text-secondary)]'>
                      {window.timezone || 'Client default'}
                    </td>
                    <td className='px-4 py-3 whitespace-nowrap text-right text-sm font-medium space-x-2'>
                      <button
                        type='button'
                        onClick={() => onEdit(window)}
                        className='text-[var(--color-accent)] hover:brightness-110'
                      >
                        Edit
                      </button>
                      <button
                        type='button'
                        onClick={() => onDelete(window.id)}
                        className='text-red-600 dark:text-red-400 hover:text-red-900 dark:hover:text-red-300'
                      >
                        Delete
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ))}
    </div>
  );
};

export default WindowList;
export { WindowList };
